import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type FocusEvent as ReactFocusEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import type { LucideIcon } from "lucide-react";

// One dropdown, two consumers (#1054): the per-page ⋮ menu and — once #1038
// lands — the profile avatar menu. Built to the WAI-ARIA *menu button* pattern,
// which is deliberately not the dialog pattern the command palette follows:
// a menu advertises Escape and arrow keys and lets Tab leave, a dialog traps.

export type HeaderMenuItem = {
  id: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  /** Explains a disabled item; rendered as title text. */
  title?: string;
  danger?: boolean;
  /** A count for this item; see `HeaderAction.badge` (#1095). */
  badge?: number;
};

export type HeaderMenuIntent = "next" | "prev" | "first" | "last" | "close" | "tab-out";

/**
 * Wrapping index arithmetic. Exported separately from the component because the
 * web unit harness is node-env `renderToStaticMarkup` — it cannot press a key,
 * so the pure part is the only part it can assert.
 */
export function cycleIndex(count: number, current: number, delta: number): number {
  if (count <= 0) return -1;
  return (((current + delta) % count) + count) % count;
}

export function menuKeyIntent(key: string): HeaderMenuIntent | null {
  if (key === "ArrowDown") return "next";
  if (key === "ArrowUp") return "prev";
  if (key === "Home") return "first";
  if (key === "End") return "last";
  if (key === "Escape") return "close";
  if (key === "Tab") return "tab-out";
  return null;
}

/**
 * Anchor the menu under the right edge of its trigger. Clamped so a trigger
 * near the viewport edge cannot push the panel off-screen.
 */
export function menuPosition(
  rect: { bottom: number; right: number },
  viewportWidth: number
): { top: number; right: number } {
  return {
    top: Math.round(rect.bottom + 6),
    right: Math.max(8, Math.round(viewportWidth - rect.right))
  };
}

type HeaderMenuListProps = {
  id: string;
  labelledBy: string;
  items: HeaderMenuItem[];
  onActivate: (item: HeaderMenuItem) => void;
  style?: CSSProperties;
  containerRef?: RefObject<HTMLDivElement | null>;
};

/**
 * The panel itself, with no portal and no effects, so a static-markup test can
 * render it and assert the roles and attributes directly.
 *
 * Disabled items carry `aria-disabled` and NOT the native `disabled` attribute.
 * That is not a style preference: `disabled` drops the button out of the focus
 * order, so a keyboard or screen-reader user could never discover that the
 * action exists or read the `title` explaining why it is unavailable.
 */
export function HeaderMenuList({ id, labelledBy, items, onActivate, style, containerRef }: HeaderMenuListProps) {
  return (
    <div
      ref={containerRef}
      id={id}
      className="header-menu-panel"
      role="menu"
      aria-labelledby={labelledBy}
      style={style}
    >
      {items.map((item) => {
        const Icon = item.icon;
        const disabled = Boolean(item.disabled);
        return (
          <button
            key={item.id}
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={`header-menu-item${item.danger ? " danger" : ""}${disabled ? " is-disabled" : ""}`}
            aria-disabled={disabled || undefined}
            title={item.title}
            onClick={() => onActivate(item)}
          >
            {Icon ? <Icon size={16} aria-hidden="true" /> : null}
            <span>{item.label}</span>
            {/* aria-hidden: the count is already inside the item's accessible
                name, which the label carries in words ("Link Rooms (23)"), so
                announcing the glyph too would read the number twice. */}
            {item.badge ? <span className="header-menu-item-badge" aria-hidden="true">{item.badge}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

export type HeaderMenuProps = {
  /** Distinguishes this menu's generated aria ids from the other one's. */
  id: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: HeaderMenuItem[];
  triggerLabel: string;
  triggerTitle?: string;
  triggerClassName?: string;
  /**
   * A count painted on the trigger itself (#1095).
   *
   * Deliberately NOT folded into `triggerLabel`: the accessible name has to
   * stay stable, or every `getByRole("button", { name })` lookup starts
   * depending on how much work happens to be outstanding. Same trade the
   * in-page popover this replaced already made — its `aria-label` was the bare
   * label and the badge was decoration — so this is parity, not a regression.
   * The count reaches assistive tech in words, on the item that resolves it.
   */
  triggerBadge?: number;
  /** The trigger's visual — the ⋮ glyph, or an avatar. */
  children: ReactNode;
};

export function HeaderMenu({
  id,
  open,
  onOpenChange,
  items,
  triggerLabel,
  triggerTitle,
  triggerClassName = "icon-button",
  triggerBadge,
  children
}: HeaderMenuProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  // ArrowUp on a closed trigger opens the menu at its LAST item, per the APG.
  const openAtEnd = useRef(false);

  const baseId = useId();
  const menuId = `${baseId}${id}-menu`;
  const triggerId = `${baseId}${id}-trigger`;

  // Guarded because the trigger can leave the document while the menu is open:
  // a page that deregisters its actions unmounts the ⋮ entirely. Focusing a
  // detached node does not throw — it silently drops focus to <body>.
  const focusTrigger = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger?.isConnected) trigger.focus();
  }, []);

  const itemNodes = useCallback((): HTMLButtonElement[] => {
    const menu = menuRef.current;
    if (!menu) return [];
    return Array.from(menu.querySelectorAll<HTMLButtonElement>("[role='menuitem']"));
  }, []);

  // Measured before the menu is shown, in the same batch as the open flag, so
  // the panel is positioned on its first paint rather than flashing at 0,0.
  const openMenu = useCallback((atEnd: boolean) => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    openAtEnd.current = atEnd;
    setPosition(menuPosition(trigger.getBoundingClientRect(), window.innerWidth));
    onOpenChange(true);
  }, [onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const nodes = itemNodes();
    const target = openAtEnd.current ? nodes[nodes.length - 1] : nodes[0];
    openAtEnd.current = false;
    target?.focus();
  }, [open, itemNodes]);

  // Light dismiss. Three things are load-bearing here:
  //   * pointerdown, not click — so one click both closes this menu and lands
  //     on whatever the user was actually reaching for.
  //   * capture phase — the floor-plan canvas and editor layers call
  //     stopPropagation freely, so a bubble-phase listener never fires there.
  //   * the trigger is excluded — otherwise this closes the menu a moment
  //     before the trigger's own onClick reopens it, and it can never be shut
  //     by clicking the button that opened it.
  // Deliberately does NOT move focus: the user is already pointing somewhere.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      onOpenChange(false);
    }
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, onOpenChange]);

  // The panel is anchored from a rect captured at open time and never tracked
  // afterwards. The header is sticky, so scrolling cannot move it — but a
  // resize can (the household switcher wraps at the compact breakpoints), and a
  // menu floating away from its trigger is worse than one that closed.
  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => onOpenChange(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open, onOpenChange]);

  // A menu with nothing in it must not stay on screen; the trigger itself is
  // about to stop rendering.
  useEffect(() => {
    if (open && items.length === 0) onOpenChange(false);
  }, [open, items.length, onOpenChange]);

  const activate = useCallback((item: HeaderMenuItem) => {
    // Activating a disabled item is a no-op and leaves the menu open, per the
    // APG — the point is that the user can read the title explaining why.
    if (item.disabled) return;
    onOpenChange(false);
    // Focus is restored BEFORE the handler runs. If onSelect navigates away and
    // takes the trigger with it (Sign out), focus lands wherever the new page
    // puts it; if the trigger survives, focus is already correct. Deferring the
    // restore instead would race the unmount.
    focusTrigger();
    item.onSelect();
  }, [onOpenChange, focusTrigger]);

  function onKeyDown(event: ReactKeyboardEvent<HTMLSpanElement>) {
    const intent = menuKeyIntent(event.key);
    if (!intent) return;

    if (!open) {
      if (intent === "next" || intent === "prev") {
        event.preventDefault();
        openMenu(intent === "prev");
      }
      return;
    }

    if (intent === "close") {
      event.preventDefault();
      // Stops the native event before it reaches Shell's window-level Escape
      // listener, which would also close the mobile navigation drawer.
      event.stopPropagation();
      onOpenChange(false);
      focusTrigger();
      return;
    }

    if (intent === "tab-out") {
      // No preventDefault: closing and focusing the trigger first means the
      // browser's own Tab handling continues from the trigger, so focus lands
      // exactly where it would have if the menu had never been open.
      onOpenChange(false);
      focusTrigger();
      return;
    }

    const nodes = itemNodes();
    if (nodes.length === 0) return;
    event.preventDefault();
    const active = nodes.findIndex((node) => node === document.activeElement);
    let next: number;
    if (intent === "first") next = 0;
    else if (intent === "last") next = nodes.length - 1;
    else if (active < 0) next = intent === "next" ? 0 : nodes.length - 1;
    else next = cycleIndex(nodes.length, active, intent === "next" ? 1 : -1);
    nodes[next]?.focus();
  }

  // Covers focus leaving for a reason the pointerdown listener does not see —
  // most usefully Ctrl+K opening the command palette over an open menu.
  // A null relatedTarget means focus went nowhere focusable, which the
  // pointerdown path already handles; closing here too would fight it.
  function onBlur(event: ReactFocusEvent<HTMLSpanElement>) {
    if (!open) return;
    const next = event.relatedTarget as Node | null;
    if (!next) return;
    if (menuRef.current?.contains(next)) return;
    if (triggerRef.current?.contains(next)) return;
    onOpenChange(false);
  }

  if (items.length === 0) return null;

  return (
    <span className="header-menu" onKeyDown={onKeyDown} onBlur={onBlur}>
      <button
        ref={triggerRef}
        id={triggerId}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={triggerLabel}
        title={triggerTitle ?? triggerLabel}
        onClick={() => (open ? onOpenChange(false) : openMenu(false))}
      >
        {children}
        {triggerBadge ? <span className="header-menu-badge" aria-hidden="true">{triggerBadge}</span> : null}
      </button>
      {/* Portaled to <body> rather than positioned inside the header. The
          header carries `backdrop-filter`, which makes it both a stacking
          context and (in Chromium) a containing block for fixed descendants —
          so an in-header menu could never paint above the page floats that sit
          at z-index 45, and `position: fixed` inside it would be measured from
          the header instead of the viewport. Raising the header's own z-index
          is not available either: the mobile nav drawer deliberately sits above
          it. Same approach as the inventory organize context menu. */}
      {open && position
        ? createPortal(
            <HeaderMenuList
              containerRef={menuRef}
              id={menuId}
              labelledBy={triggerId}
              items={items}
              onActivate={activate}
              style={{ top: position.top, right: position.right }}
            />,
            document.body
          )
        : null}
    </span>
  );
}
